import { func, number, shape, string } from 'prop-types'
import { Component } from 'react'
import { connectRange } from 'react-instantsearch-dom'

import { IS_NODE } from 'shared/consts/env'
import { PRODUCT_FILTER_RESET_SFX } from 'shared/experiments/components/NewSerp/consts'
import { subscribe } from 'shared/store'
import { PRODUCT_FILTER_RESET } from 'shared/store/ducks/events'
import { normalizeRangeInputValue } from 'shared/utils/algolia/filters'
import InputRange from 'views/components/molecules/InputRange'
import { PRICE_DEFAULT_VALUES } from 'views/components/organisms/AlgoliaInstantSearch/ProductFilter/consts'
import Collapsible from 'views/components/organisms/Collapsible'
import { BREAKPOINT_SMALL_TINY } from 'views/consts'
/**
 * When `defaultValues` prop provided, it overrides the range min and max
 * that received from the connected props of the component.
 * Otherwise it gets a min and max for all the hits in the matching records set.
 */
class InputRangeConnected extends Component {
  static getMiddleValue(min, value, max) {
    return [min, value, max].sort((a, b) => a - b)[1]
  }

  state = this.getInitialState()

  getInitialState() {
    const { min, max, currentRefinement, defaultValues, rangeValues } =
      this.props
    const { min: defaultMin, max: defaultMax } = defaultValues
    const { min: rMin, max: rMax } = rangeValues
    const normalizedMin = min / 100
    const normalizedMax = max / 100
    const rangeMin = typeof rMin !== 'undefined' ? rMin : normalizedMin
    const rangeMax = typeof rMax !== 'undefined' ? rMax : normalizedMax
    const values = [
      typeof defaultMin !== 'undefined' ? defaultMin : normalizedMin,
      typeof defaultMax !== 'undefined' ? defaultMax : normalizedMax,
    ]

    return {
      rangeMin,
      rangeMax,
      values,
      currentRefinement,
      noResults: false,
      applied: false,
      minInput: null,
      maxInput: null,
    }
  }

  componentDidMount() {
    const { attribute } = this.props

    subscribe.after(PRODUCT_FILTER_RESET, () => {
      const { rangeMin, rangeMax, minInput, maxInput } = this.state

      this.setState({
        values: [rangeMin, rangeMax],
        applied: false,
      })

      if (minInput) {
        minInput.value = rangeMin
      }

      if (maxInput) {
        maxInput.value = rangeMax
      }
    })

    if (attribute) {
      subscribe.after(`${PRODUCT_FILTER_RESET_SFX}${attribute}`, () => {
        const { rangeMin, rangeMax, minInput, maxInput } = this.state

        this.setState({
          values: [rangeMin, rangeMax],
          applied: false,
        })

        if (minInput) {
          minInput.value = rangeMin
        }

        if (maxInput) {
          maxInput.value = rangeMax
        }
      })
    }
  }

  componentDidUpdate(prevProps) {
    const {
      values: [min, max],
      applied,
    } = this.state
    const {
      rangeValues: { min: prevMin, max: prevMax },
    } = prevProps
    const {
      rangeValues: { min: rangeMin, max: rangeMax },
    } = this.props

    if (prevMin !== rangeMin || prevMax !== rangeMax) {
      let newMin = rangeMin
      let newMax = rangeMax

      if (applied) {
        newMin = InputRangeConnected.getMiddleValue(rangeMin, min, rangeMax)
        newMax = InputRangeConnected.getMiddleValue(rangeMin, max, rangeMax)
      }

      if (newMin < newMax) {
        this.setState({
          values: [newMin, newMax],
          rangeMin,
          rangeMax,
        })
      }
    }
  }

  refine({ min, max }) {
    const { currentRefinement } = this.state
    const { refine } = this.props

    // Try to refine: fails when min/max is out of boundaries
    try {
      refine({
        ...currentRefinement,
        min: min * 100,
        max: max * 100,
      })
      // eslint-disable-next-line no-empty
    } catch (_) {}
  }

  updateFilter = () => {
    const {
      rangeMin,
      rangeMax,
      values: [min, max],
    } = this.state

    const finalMinValue = InputRangeConnected.getMiddleValue(
      rangeMin,
      min,
      rangeMax
    )
    const finalMaxValue = InputRangeConnected.getMiddleValue(
      rangeMin,
      max,
      rangeMax
    )

    if (finalMaxValue === rangeMin || finalMinValue === rangeMax) {
      this.refine({ min: rangeMax, max: 0 })
      this.setState({ applied: true })
    } else if (finalMaxValue >= finalMinValue) {
      this.refine({ min: finalMinValue, max: finalMaxValue })
      this.setState({ applied: true })
    }
  }

  /**
   * Triggers on a Rheostat knobs tunings
   */
  updateValues = ({ values: [min, max] }) => {
    const { currentRefinement, minInput, maxInput } = this.state

    const normMinValue = min * 100
    const normMaxValue = max * 100

    if (
      normMinValue <= normMaxValue &&
      (currentRefinement.min !== normMinValue ||
        currentRefinement.max !== normMaxValue)
    ) {
      this.setState({
        values: [min, max],
      })

      if (minInput) {
        minInput.value = min
      }

      if (maxInput) {
        maxInput.value = max
      }
    }
  }

  onChange = ({ event, handleMinInput, forceRefine }) => {
    const {
      target,
      target: { value },
    } = event

    const {
      values: [min, max],
      minInput,
      maxInput,
    } = this.state
    const input = handleMinInput ? minInput : maxInput
    const newValue = normalizeRangeInputValue(value)
    const condition = handleMinInput ? newValue <= max : min <= newValue
    const newValues = handleMinInput ? [newValue, max] : [min, newValue]

    if (condition) {
      this.setState({
        values: newValues,
      })

      if (forceRefine) {
        this.refine({ min: newValues[0], max: newValues[1] })
        this.setState({ applied: true })
      }

      target.value = newValue.toFixed(2)
    } else {
      target.value = handleMinInput ? min : max
    }

    if (!input) {
      if (handleMinInput) {
        this.setState({
          minInput: target,
        })
      } else {
        this.setState({
          maxInput: target,
        })
      }
    }
  }

  isSmallTinyView = () =>
    !IS_NODE && window.matchMedia(BREAKPOINT_SMALL_TINY).matches

  onMinChange = event => {
    this.onChange({
      event,
      handleMinInput: true,
      forceRefine: this.isSmallTinyView(),
    })
  }

  onMaxChange = event => {
    this.onChange({
      event,
      handleMinInput: false,
      forceRefine: this.isSmallTinyView(),
    })
  }

  onMinEnterPress = event => {
    if (event.keyCode !== 13) {
      return
    }

    this.onChange({ event, handleMinInput: true, forceRefine: true })
  }

  onMaxEnterPress = event => {
    if (event.keyCode !== 13) {
      return
    }

    this.onChange({ event, handleMinInput: false, forceRefine: true })
  }

  normalizedValues = () => {
    const { values } = this.state

    return values.map(value => parseFloat(value.toFixed(2)))
  }

  render() {
    const { label, attribute, ...rest } = this.props
    const { rangeMin, rangeMax } = this.state

    return (
      <Collapsible
        forceDisplayWhenCollapsed
        id={`input-range-control-${attribute}`}
        label={label}
        expanded
        inFilter
        headingLevel="p"
      >
        <InputRange
          {...rest}
          events={{
            onClick: this.updateFilter,
            onValuesUpdated: this.updateValues,
            onMinChange: this.onMinChange,
            onMaxChange: this.onMaxChange,
            onSliderDragEnd: this.updateFilter,
            onMinEnterPress: this.onMinEnterPress,
            onMaxEnterPress: this.onMaxEnterPress,
          }}
          values={this.normalizedValues()}
          maxRange={[rangeMin, rangeMax]}
          step={0.01}
        />
      </Collapsible>
    )
  }
}

InputRangeConnected.propTypes = {
  ...InputRange.propTypes,
  min: number,
  max: number,
  label: string.isRequired,
  attribute: string.isRequired,
  currentRefinement: shape({}).isRequired,
  refine: func.isRequired,
  defaultValues: shape({
    min: number,
    max: number,
  }),
}

InputRangeConnected.defaultProps = {
  ...InputRange.defaultProps,
  min: PRICE_DEFAULT_VALUES.min,
  max: PRICE_DEFAULT_VALUES.max,
  defaultValues: {},
}

export default connectRange(InputRangeConnected)
